import queryClient from "@/lib/queryClient";
import authStore from "@/stores/authStore";
import { useAuthContext } from "@cs_teamwaydev/mailvista-sdk_react";
import { useTranslation } from "react-i18next";
import { useToast } from "./utils/useToast";

type useGetAuthDataResponse = {
    handleLogin: () => void;
    handleLogout: () => void;
    isAuthenticated: boolean;
};

const REDIRECT_IN_FLIGHT_MESSAGE = "Should have been redirected";

const isRedirectInFlight = (error: unknown): boolean =>
    error instanceof Error &&
    error.message.includes(REDIRECT_IN_FLIGHT_MESSAGE);

const useGetAuthData = (): useGetAuthDataResponse => {
    const { loginRedirect, logoutRedirect, isAuthenticated } = useAuthContext();
    const { toast } = useToast();
    const { t } = useTranslation();

    const handleLogin = async () => {
        try {
            await loginRedirect();
        } catch (error) {
            if (isRedirectInFlight(error)) {
                return;
            }

            console.error(error);
            toast({
                title: t("errors.login"),
                description: t("errors.global.description"),
                variant: "error",
            });
        }
    };

    const handleLogout = async () => {
        if (authStore.state.isLoggingOut) {
            return;
        }

        authStore.setState((prevState) => ({
            ...prevState,
            isLoggingOut: true,
        }));
        await queryClient.cancelQueries();

        try {
            await logoutRedirect();
        } catch (error) {
            if (isRedirectInFlight(error)) {
                return;
            }

            authStore.setState((prevState) => ({
                ...prevState,
                isLoggingOut: false,
            }));
            console.error(error);
            toast({
                title: t("errors.logout"),
                description: t("errors.global.description"),
                variant: "error",
            });
        }
    };

    return {
        handleLogin,
        handleLogout,
        isAuthenticated,
    };
};

export default useGetAuthData;
